<template>
  <div class="p-5 pt-3">
    <p class="font-semibold text-3xl">
      List Data Sales Plan
    </p>
    <div class="mt-8">
      <div class="bg-white rounded-2xl p-8">
        <div class="flex flex-wrap flex-row items-center justify-start">
          <span class="font-semibold text-lg mr-16">Filter</span>
          <date-picker
            v-if="type === 'monthly'"
            class="flex-grow ml-16"
            type="month"
            format="MMMM"
            value-type="format"
            placeholder="Month"
            v-model="filter.month"
          />
          <date-picker
            class="flex-grow"
            :class="{
              'ml-16': type === 'annual',
              'mx-8': type === 'monthly'
            }"
            type="year"
            value-type="format"
            placeholder="Period"
            v-model="filter.period"
          />
          <select-list
            v-if="type === 'annual'"
            class="flex-grow mx-8"
            :options="options"
            placeholder="Revision"
            v-model="filter.revision"
          />
          <vs-button
            class="filter-button"
            color="secondary"
            @click="fetchSalesPlanList"
          >
            Filter
          </vs-button>
        </div>
      </div>
    </div>
    <table-top
      :page-size-start="false"
      class="mt-8"
      @fetch="fetchSalesPlanList"
    >
      <template #start>
        <div class="bg-white rounded-2xl py-4 px-5">
          <div class="flex flex-wrap flex-row items-center justify-start">
            <span class="font-semibold">Type List</span>
            <radio-button
              class="ml-8"
              v-model="type"
              vs-name="annual"
              vs-value="annual"
            >
              Annual
            </radio-button>
            <radio-button
              class="ml-4"
              v-model="type"
              vs-name="monthly"
              vs-value="monthly"
            >
              Monthly
            </radio-button>
          </div>
        </div>
      </template>
    </table-top>
    <div class="my-8 flex flex-row">
      <div class="ml-0 w-full">
        <data-table
          :loading="isBusy"
          :table-header="tableHeaders"
          :table-data="tableData"
          :sort-by="filter.sortBy"
          :sort-dir="filter.sortDir"
          body-height="100vh"
          @sort="sort"
        >
          <template #action="{ row }">
            <img
              class="w-6 mx-auto cursor-pointer"
              :src="require('@/assets/shipo-sales-plan-icon-detail.svg')"
              @click="detail(row.id, row.period)"
            >
          </template>
          <template #upload_date="{ row }">
            <span>{{ moment(row.created_at).format('DD-MMM-YYYY HH:mm:ss') }}</span>
          </template>
          <template #month="{ row }">
            <span>{{ moment(row.created_at).format('MMMM') }}</span>
          </template>
          <template #no="{ index }">
            <span>{{ index + 1 }}</span>
          </template>
        </data-table>
      </div>
    </div>
    <table-bottom @fetch="fetchSalesPlanList" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import DataTable from '@/components/dataTable'
import DatePicker from '@/components/datePicker'
import RadioButton from '@/components/radioButton'
import SelectList from '@/components/selectList'
import TableBottom from '@/components/tableBottom'
import TableTop from '@/components/tableTop'
import { PAGE_TITLE } from '@/fixtures/Constants'

export default {
  metaInfo: {
    title: `${PAGE_TITLE} Sales Plan List`
  },
  components: {
    RadioButton,
    DatePicker,
    SelectList,
    TableBottom,
    TableTop,
    DataTable
  },
  data() {
    return {
      type: 'annual',
      filter: {
        revision: '',
        period: '',
        month: '',
        sortBy: '',
        sortDir: ''
      },
      options:[],
      tableHeaders: [],
      tableData: [],
      isBusy: false
    }
  },
  mounted() {
    this.fetchSalesPlanList()
    this.fetchTableHeader()
    this.fetchRevisionList()
  },
  computed: {
    ...mapGetters({
      salesPlanList: 'SalesPlan/salesPlanList',
      revisionList: 'SalesPlan/revisionList'
    })
  },
  watch: {
    salesPlanList: {
      immediate: true,
      handler(val) {
        this.tableData = val
      }
    },
    type: {
      immediate: true,
      handler() {
        this.filter = {
          revision: '',
          period: '',
          month: '',
          sortBy: '',
          sortDir: ''
        }
        this.fetchSalesPlanList()
        this.fetchTableHeader()
        this.fetchRevisionList()
      }
    },
    revisionList: {
      handler(val) {
        this.options = val.map(item => ({ text: item, value: item }))
      }
    }
  },
  methods: {
    async fetchRevisionList() {
      try {
        await this.$store.dispatch('SalesPlan/getRevisionList')
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch Revision list, please try again')
      }
    },
    async fetchSalesPlanList() {
      try {
        this.isBusy = true

        await this.$store.dispatch('SalesPlan/getSalesPlanList', {
          type: this.type,
          ...this.filter,
          sortBy: this.filter.sortBy === 'upload_date'
            ? 'created_at'
            : this.filter.sortBy,
          month: this.filter.month
            ? this.moment().month(this.filter.month ).format("M")
            : ''
        })

        this.isBusy = false
      } catch (e) {
        this.isBusy = false
        this.$vs.notify({
          title: 'Failed',
          text: 'Something went wrong',
          color: 'danger',
          icon: 'close',
          position: 'top-right'
        })
      }
    },
    fetchTableHeader() {
      if (this.type === 'annual') {
        this.tableHeaders = [
          { key: 'action', label: 'Action' },
          { key: 'no', label: 'No' },
          { key: 'upload_date', label: 'Upload Date', sortable: true },
          { key: 'period', label: 'Period', sortable: true },
          { key: 'revision', label: 'Revision', sortable: true },
          { key: 'filename', label: 'File Name', sortable: true }
        ]
      } else {
        this.tableHeaders = [
          { key: 'action', label: 'Action' },
          { key: 'no', label: 'No' },
          { key: 'upload_date', label: 'Upload Date', sortable: true },
          { key: 'month', label: 'Month', sortable: true },
          { key: 'period', label: 'Period', sortable: true },
          { key: 'filename', label: 'File Name', sortable: true }
        ]
      }
    },
    sort(sortBy, sortDir) {
      this.filter.sortBy = sortBy
      this.filter.sortDir = sortDir

      this.fetchSalesPlanList()
    },
    detail(id, period) {
      if (this.type === 'annual') {
        this.$router.push({ name: 'SalesPlanDetailAnnual', params: { id, period } })
      } else {
        this.$router.push({ name: 'SalesPlanDetailMonthly', params: { id, period } })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.filter-button {
  @apply py-4 #{!important};
  @apply px-16;
  -webkit-box-shadow: 0 8px 25px -8px rgba(var(--vs-secondary),1);
  box-shadow: 0 8px 25px -8px rgba(var(--vs-secondary),1);

  &:focus {
    @apply border-none;
    @apply outline-none;
  }
}
</style>
